/* eslint-disable max-lines -- one component forwarding the same pagination-footer prop list DataTablePagination already takes; splitting the forwarding would just fragment one cohesive prop list. */
import type { RowData } from '@tanstack/react-table';
import * as React from 'react';

import { DataTablePagination } from '../DimeDataTable/DataTablePagination.js';
import { DataTableToolbar } from '../DimeDataTable/DataTableToolbar.js';
import {
  DimeTable,
  DimeTableBody,
  DimeTableCell,
  DimeTableRow,
  SkeletonRows,
} from '../DimeTable/index.js';
import { cn } from '../lib/utils.js';
import { useBoundedTableHeight } from '../table-internals/use-bounded-table-height.js';
import { useRowKeyboardNavigation } from '../table-internals/use-row-keyboard-navigation.js';
import type { DimeNestedTableProps, ParentRow } from './DimeNestedTable.types.js';
import { expandCellClass } from './expand-cell-class.js';
import { NestedExpandAllContext } from './NestedExpandAllContext.js';
import { NestedTableHeader } from './NestedTableHeader.js';
import { NestedTableRow } from './NestedTableRow.js';
import { useExpandAllRows } from './use-expand-all-rows.js';
import { useNestedParentTable } from './use-nested-parent-table.js';
import { useNestedRowKeyDown } from './use-nested-row-key-down.js';

export type { DimeNestedTableProps, ParentColumnDef } from './DimeNestedTable.types.js';

export function DimeNestedTable<TParent extends RowData, TChild>(
  props: Readonly<DimeNestedTableProps<TParent, TChild>>,
) {
  const {
    childRowsByParentId,
    loadingParentIds,
    renderChildRows,
    onExpandRow,
    searchColumn,
    searchPlaceholder,
    onSearchChange,
    searchValue,
    filters,
    actions,
    showViewOptions = false,
    showExpandAll = false,
    filterChips,
    hasNextPage = false,
    hasPreviousPage = false,
    onNextPage,
    onPreviousPage,
    onFirstPage,
    onLastPage,
    showFirstLast,
    pageIndex,
    pageSize,
    pageSizeOptions,
    onPageSizeChange,
    totalCount,
    isLoading = false,
    loadingRowCount = 20,
    emptyMessage = 'No results.',
    className,
    maxBodyHeight,
  } = props;
  const footerRef = React.useRef<HTMLDivElement>(null);
  const {
    containerRef,
    style: heightStyle,
    isBounded,
  } = useBoundedTableHeight(maxBodyHeight, {
    reserveBelowRef: footerRef,
  });

  const {
    table,
    handleToggleExpand,
    resolvedOnExpandedChange,
    isRequested,
    markRequested,
    clearRequested,
    hasConsumerReportedFetching,
    readChildRows,
    readLoadingParentIds,
  } = useNestedParentTable<TParent, TChild>(props);

  const rows = table.getRowModel().rows;
  // Visible, not total. Hiding a parent column via the Columns menu would otherwise leave the
  // nested panel, the empty-state row and the skeletons spanning a phantom column, distorting
  // every column width — and parentColumnCount is part of the renderChildRows contract.
  const visibleColumnCount = table.getVisibleFlatColumns().length;
  // No pagination row model is registered, so `rows` is only ever the page the consumer passed, and
  // the row's absolute position and the set total are both unknowable here. Gate on the callbacks
  // rather than hasNextPage/hasPreviousPage: those are false on the LAST page too, which would
  // announce "1 of 2" for the final 2 of 100 -- the same wrong count this avoids elsewhere.
  const isServerPaginated = onNextPage !== undefined || onPreviousPage !== undefined;

  const onKeyDown = useNestedRowKeyDown<TParent>({ onToggleExpand: handleToggleExpand });
  const {
    resolvedActiveRowId,
    registerRowElement,
    setActiveRowId,
    handleRowBlur,
    handleRowKeyDown,
  } = useRowKeyboardNavigation<ParentRow<TParent>>({ rows, onKeyDown });

  const { expandAllRows, collapseAllRows } = useExpandAllRows<TParent, TChild>({
    rows,
    readChildRows,
    readLoadingParentIds,
    isRequested,
    markRequested,
    clearRequested,
    hasConsumerReportedFetching,
    onExpandedChange: resolvedOnExpandedChange,
    onExpandRow,
  });

  // getCanExpand(): a parent whose children resolved to [] keeps its expanded flag but has nothing open.
  const anyRowExpanded = rows.some((row) => row.getCanExpand() && row.getIsExpanded());
  const hasExpandableRows = rows.some((row) => row.getCanExpand());

  const expandAllState = showExpandAll
    ? {
        anyRowExpanded,
        hasExpandableRows,
        onExpandAll: expandAllRows,
        onCollapseAll: collapseAllRows,
      }
    : null;

  const showToolbar = Boolean(searchColumn || filters || filterChips || actions || showViewOptions);

  return (
    <div className={cn('min-w-0 w-full', className)}>
      {showToolbar && (
        <DataTableToolbar
          table={table}
          searchColumn={searchColumn}
          searchPlaceholder={searchPlaceholder}
          onSearchChange={onSearchChange}
          searchValue={searchValue}
          filters={filters}
          actions={actions}
          showViewOptions={showViewOptions}
          filterChips={filterChips}
        />
      )}
      {/* DimeTable/Table already wraps its own overflow-x-auto div; this is defense-in-depth/consistency, not what enables scrolling. */}
      <div
        ref={containerRef}
        className={cn(
          'rounded-md border border-border',
          isBounded ? 'overflow-hidden' : 'overflow-x-auto',
        )}
      >
        <DimeTable
          role="treegrid"
          containerClassName={isBounded ? 'overflow-auto' : undefined}
          containerStyle={heightStyle}
        >
          <NestedExpandAllContext.Provider value={expandAllState}>
            <NestedTableHeader headerGroups={table.getHeaderGroups()} sticky={isBounded} />
          </NestedExpandAllContext.Provider>
          <DimeTableBody>
            {isLoading && rows.length === 0 ? (
              <SkeletonRows rowCount={loadingRowCount} columnCount={visibleColumnCount} />
            ) : rows.length > 0 ? (
              rows.map((row, rowIndex) => (
                <NestedTableRow<TParent, TChild>
                  key={row.id}
                  row={row}
                  rowIndex={rowIndex}
                  isServerPaginated={isServerPaginated}
                  rowCount={rows.length}
                  visibleColumnCount={visibleColumnCount}
                  childRowsByParentId={childRowsByParentId}
                  loadingParentIds={loadingParentIds}
                  renderChildRows={renderChildRows}
                  cellClassName={expandCellClass}
                  isActiveRow={resolvedActiveRowId === row.id}
                  registerRowElement={registerRowElement}
                  onRowFocus={setActiveRowId}
                  onRowBlur={handleRowBlur}
                  onRowKeyDown={handleRowKeyDown}
                />
              ))
            ) : (
              <DimeTableRow>
                <DimeTableCell
                  colSpan={visibleColumnCount}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </DimeTableCell>
              </DimeTableRow>
            )}
          </DimeTableBody>
        </DimeTable>
      </div>
      <div ref={footerRef} data-slot="table-below-reserve">
        <DataTablePagination
          table={table}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
          onFirstPage={onFirstPage}
          onLastPage={onLastPage}
          showFirstLast={showFirstLast}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={onPageSizeChange}
          rowCount={rows.length}
          totalCount={totalCount}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
